import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import type { AuthConfig } from "./config";

/** In-memory storage for Cognito session when "remember me" is off. Session is lost on tab close. */
function createMemoryStorage(): Storage {
  const store: Record<string, string> = {};
  return {
    getItem: (key: string) => store[key] ?? null,
    setItem: (key: string, value: string) => {
      store[key] = value;
    },
    removeItem: (key: string) => {
      delete store[key];
    },
    clear: () => {
      for (const key of Object.keys(store)) delete store[key];
    },
    get length() {
      return Object.keys(store).length;
    },
    key: (index: number) => Object.keys(store)[index] ?? null,
  };
}

export function createUserPool(
  config: AuthConfig,
  storage?: Storage
): CognitoUserPool {
  const poolConfig: {
    UserPoolId: string;
    ClientId: string;
    Storage?: Storage;
  } = {
    UserPoolId: config.user_pool_id,
    ClientId: config.client_id,
  };
  if (storage !== undefined) {
    poolConfig.Storage = storage;
  }
  return new CognitoUserPool(poolConfig);
}

export function signIn(
  config: AuthConfig,
  email: string,
  password: string,
  rememberMe: boolean = true
): Promise<string> {
  const pool =
    rememberMe === false
      ? createUserPool(config, createMemoryStorage())
      : createUserPool(config);
  const user = new CognitoUser({
    Username: email.trim(),
    Pool: pool,
  });
  const details = new AuthenticationDetails({
    Username: email.trim(),
    Password: password,
  });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        resolve(idToken);
      },
      onFailure: (err) => {
        const msg = err.message || "Sign-in failed.";
        reject(new Error(msg));
      },
    });
  });
}

/**
 * Tries to restore a session from Cognito's default storage (localStorage).
 * Returns the current ID token if a valid session exists (refreshing if needed); otherwise null.
 */
export function tryRestoreSession(config: AuthConfig): Promise<string | null> {
  const pool = createUserPool(config);
  const user = pool.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: unknown, session: unknown) => {
      if (err || !session || typeof (session as { getIdToken?: () => unknown }).getIdToken !== "function") {
        resolve(null);
        return;
      }
      try {
        const idToken = (session as { getIdToken: () => { getJwtToken: () => string } }).getIdToken().getJwtToken();
        resolve(idToken);
      } catch {
        resolve(null);
      }
    });
  });
}

/**
 * Clears the Cognito session from default storage (localStorage) so the user is not auto-restored.
 */
export function signOutFromCognito(config: AuthConfig): void {
  const pool = createUserPool(config);
  const user = pool.getCurrentUser();
  if (user) user.signOut();
}

export function registerViaBackend(
  email: string,
  password: string,
  discountCode: string
): Promise<void> {
  return fetch("/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: email.trim(),
      password,
      discount_code: discountCode.trim().slice(0, 18),
    }),
  }).then(async (res) => {
    const text = await res.text();
    let detail = "";
    try {
      const body = JSON.parse(text) as { detail?: string };
      detail = body.detail ?? text;
    } catch {
      detail = text || `Request failed (${res.status})`;
    }
    if (!res.ok) {
      throw new Error(detail);
    }
  });
}
